/**
 * Pure helpers for checkout attendee validation and cart split actions.
 * No API calls; same logic as previous inline code in handleCheckout.
 */

/**
 * Validate attendee data for all line items.
 * @param {Object} cart - Cart with lineItems
 * @param {Object} attendeesData - Map of lineItemId -> array of attendee objects { guestName, guestEmail }
 * @returns {{ hasErrors: boolean, errors: Object }}
 */
export function validateAttendees(cart, attendeesData) {
  const errors = {};
  let hasErrors = false;

  (cart?.lineItems ?? []).forEach((item) => {
    if (!item || !item.id) return;

    const itemAttendees = attendeesData[item.id] || [];
    const quantity = item.quantity || 0;

    if (itemAttendees.length < quantity) {
      hasErrors = true;
      errors[item.id] = `Please fill in attendee details for all ${quantity} unit(s) of this product.`;
    } else {
      const itemErrors = {};
      itemAttendees.forEach((attendee, idx) => {
        const attendeeErrors = {};
        if (!attendee.guestName?.trim()) {
          attendeeErrors.guestName = 'Guest name is required';
        }
        if (!attendee.guestEmail?.trim()) {
          attendeeErrors.guestEmail = 'Guest email is required';
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(attendee.guestEmail.trim())) {
            attendeeErrors.guestEmail = 'Please enter a valid email';
          }
        }
        if (Object.keys(attendeeErrors).length > 0) {
          itemErrors[idx] = attendeeErrors;
          hasErrors = true;
        }
      });
      if (Object.keys(itemErrors).length > 0) {
        errors[item.id] = itemErrors;
      }
    }
  });

  return { hasErrors, errors };
}

/**
 * Build cart update actions to split line items by attendees (one line per attendee, qty 1).
 * Same shape as used by updateCart(); caller still invokes updateCart(actions).
 * @param {Object} cart - Cart with lineItems
 * @param {Object} attendeesData - Map of lineItemId -> array of attendee objects
 * @returns {Array<Object>} Cart update actions for updateCart()
 */
export function buildAttendeeSplitActions(cart, attendeesData) {
  const cartUpdateActions = [];

  (cart?.lineItems ?? []).forEach((item) => {
    if (!item || !item.id) return;
    const itemAttendees = attendeesData[item.id] || [];

    if (itemAttendees.length > 0) {
      cartUpdateActions.push({
        action: 'removeLineItem',
        lineItemId: item.id,
      });

      itemAttendees.forEach(() => {
        const addLineItemAction = {
          action: 'addLineItem',
          productId: item.productId,
          variantId: item.variant?.id || 1,
          quantity: 1,
        };
        if (item.priceMode === 'ExternalPrice' && item.price?.value) {
          addLineItemAction.externalPrice = item.price.value;
        }
        if (item.custom) {
          addLineItemAction.custom = {
            type: item.custom.type,
            fields: item.custom.fields,
          };
        }
        cartUpdateActions.push(addLineItemAction);
      });
    }
  });

  return cartUpdateActions;
}
